import { API_CONFIG } from '../../config/api';
import { apiClient } from '../../lib/api-client';
import {
  FirmBalanceUsageResponse,
  FirmPricingDetail,
  FirmPlanConfigResponse,
  ListFirmsQueryParams,
  ListFirmsResponse,
  PlanConfig,
  UpdateBalanceUsageRequest,
  UpdatePlanConfigRequest,
  UpgradePlanRequest,
  UpgradePlanResponse,
} from '../../types/firm';

/**
 * Firm API Service
 * All firm-related API calls should be defined here
 */
export const firmApi = {
  /**
   * List firms with optional filters
   */
  listFirms: (params?: ListFirmsQueryParams): Promise<ListFirmsResponse> => {
    return apiClient.get(API_CONFIG.ENDPOINTS.FIRMS.LIST, { params });
  },

  /**
   * Get firm pricing detail by ID (full details including plan config and balance usage)
   */
  getFirmPricingDetail: (firmId: number): Promise<FirmPricingDetail> =>
    apiClient.get(`${API_CONFIG.ENDPOINTS.FIRMS.DETAIL}/${firmId}`),

  /**
   * Get plan configuration only (lightweight, no balance/usage data)
   */
  getPlanConfig: (firmId: number): Promise<FirmPlanConfigResponse> =>
    apiClient.get(`${API_CONFIG.ENDPOINTS.FIRMS.UPDATE_PLAN_CONFIG}/${firmId}/plan-config`),

  /**
   * Get balance and usage information only
   */
  getBalanceUsage: (firmId: number): Promise<FirmBalanceUsageResponse> =>
    apiClient.get(`${API_CONFIG.ENDPOINTS.FIRMS.UPDATE_PLAN_CONFIG}/${firmId}/balance-usage`),

  /**
   * Update plan configuration for a firm
   * Returns the updated PlanConfig (not the full FirmPricingDetail)
   * Credit adjustments are calculated and applied immediately
   */
  updatePlanConfig: (firmId: number, data: UpdatePlanConfigRequest): Promise<PlanConfig> =>
    apiClient.patch(`${API_CONFIG.ENDPOINTS.FIRMS.UPDATE_PLAN_CONFIG}/${firmId}/plan-config`, data),

  /**
   * Upgrade firm plan to a predefined plan type (free or full)
   * Sets billing anchor to current date and applies credit adjustments
   */
  upgradePlan: (firmId: number, data: UpgradePlanRequest): Promise<UpgradePlanResponse> =>
    apiClient.post(`${API_CONFIG.ENDPOINTS.FIRMS.UPDATE_PLAN_CONFIG}/${firmId}/upgrade-plan`, data),

  /**
   * Update balance usage (direct credit adjustments without plan changes)
   */
  updateBalanceUsage: (firmId: number, data: UpdateBalanceUsageRequest): Promise<FirmBalanceUsageResponse> =>
    apiClient.patch(`${API_CONFIG.ENDPOINTS.FIRMS.UPDATE_PLAN_CONFIG}/${firmId}/balance-usage`, data),

  // Future API methods can be added here:
  // createFirm: (data: CreateFirmRequest): Promise<FirmDetail> =>
  //   apiClient.post(API_CONFIG.ENDPOINTS.FIRMS.LIST, data),
  // updateFirm: (id: string, data: UpdateFirmRequest): Promise<FirmDetail> =>
  //   apiClient.put(`${API_CONFIG.ENDPOINTS.FIRMS.LIST}/${id}`, data),
  // deleteFirm: (id: string): Promise<void> =>
  //   apiClient.delete(`${API_CONFIG.ENDPOINTS.FIRMS.LIST}/${id}`),
};

